import { isAccountCoinAccount } from "@/app/(app)/[userId]/utils";

import { DESO_FOUNDATION_TOKENS, DESO_WRAPPED_ASSETS } from "./constants";
import { shortenLongWord } from "./helpers";
import type { Maybe, PartialAccount } from "./types";

export const adjectives: string[] = [
  "Cosmic",
  "Lunar",
  "Zephyr",
  "Nebula",
  "Galactic",
  "Pixel",
  "Jovial",
  "Techno",
  "Energetic",
  "Whimsical",
  "Celestial",
  "Solar",
  "Crypto",
  "Dynamic",
  "Aurora",
  "Rainbow",
  "Plasma",
  "Digital",
  "Zenith",
  "Electric",
  "Nexus",
  "Unity",
  "Infinite",
  "Gravity",
  "Quantum",
  "Stellar",
  "Ethereal",
  "Luminous",
  "Vibrant",
  "Sizzling",
  "Mysterious",
  "Pulsar",
  "Velvety",
  "Sparkling",
  "Wondrous",
  "Hyper",
  "Turbocharged",
  "Dazzling",
  "Kaleidoscopic",
  "Serendipitous",
  "Quirky",
  "Effervescent",
  "Harmonic",
  "Jubilant",
  "Spirited",
  "Flamboyant",
  "Peculiar",
  "Eccentric",
  "Splendiferous",
  "Captivating",
  "Enigmatic",
  "Mystical",
  "Utopian",
  "Pizzazz",
  "Blissful",
  "Hypnotic",
  "Vivacious",
  "Radiant",
];

export const animals: string[] = [
  "Cheetah",
  "Lynx",
  "Zebra",
  "Narwhal",
  "Giraffe",
  "Penguin",
  "Jaguar",
  "Tiger",
  "Eagle",
  "Walrus",
  "Chameleon",
  "Gazelle",
  "Squirrel",
  "Cobra",
  "Dolphin",
  "Alpaca",
  "Raccoon",
  "Gorilla",
  "Panda",
  "Dingo",
  "Seahorse",
  "Quokka",
  "Elephant",
  "Nightingale",
  "Vulture",
  "Mongoose",
  "Starfish",
  "Newt",
  "Cockatoo",
  "Quail",
  "Iguana",
  "Gopher",
  "Nuthatch",
  "Uakari",
  "Centipede",
  "Quetzal",
  "Dragonfly",
  "Emu",
  "Pangolin",
  "Lemur",
  "Koala",
  "Axolotl",
  "Platypus",
  "Okapi",
  "Chinchilla",
  "Mantis",
  "FennecFox",
  "Capybara",
  "Peacock",
  "Firefly",
  "Pufferfish",
  "Hummingbird",
  "Snail",
  "Gargoyle",
  "Sasquatch",
  "SwoopingOwl",
  "Jellyfish",
  "Butterfly",
  "Bumblebee",
  "CosmicCrane",
  "Wallaby",
  "Dingo",
  "Nudibranch",
  "Tarantula",
  "Zebrafish",
  "Raccoon",
  "Quail",
  "Vulture",
  "Elephant",
  "Gecko",
  "Penguin",
  "Seahorse",
  "Zonkey",
  "Moose",
  "Turtle",
  "Viper",
  "Zebu",
  "Skunk",
  "Ibis",
  "Salamander",
  "Flamingo",
  "Jaguar",
  "Snail",
];

export const generateRandomUsername = (): string => {
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const animal = animals[Math.floor(Math.random() * animals.length)];
  const randomNumber = Math.floor(Math.random() * 99) + 1;

  const generatedName = `${adjective}${animal}${randomNumber}`;

  if (generatedName.length <= 25) {
    return generatedName;
  }

  return `${adjective}${animal}${randomNumber}`;
};

export const getUserName = ({
  customPrefix,
  fallback = "N/A",
  account,
  truncateLength = 25,
}: {
  customPrefix?: string;
  account?: PartialAccount | null;
  fallback?: string;
  truncateLength?: number;
}) => {
  const publicKey = account?.publicKey || "";
  const username =
    DESO_WRAPPED_ASSETS[publicKey] ??
    DESO_FOUNDATION_TOKENS[publicKey] ??
    account?.username;
  const finalPrefix =
    customPrefix ?? (isAccountCoinAccount(account) ? "$" : "@");

  if (!username && fallback !== "N/A") {
    const result = `${finalPrefix}${fallback}`;
    return result.length > truncateLength
      ? `${result.slice(0, truncateLength)}...`
      : result;
  }

  const result =
    (username ? `${finalPrefix}${username}` : shortenLongWord(publicKey)) ||
    `${finalPrefix}${fallback}`;

  return result.length > truncateLength
    ? `${result.slice(0, truncateLength)}...`
    : result;
};

type getUserDisplayNameProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraData?: any;
  username?: Maybe<string>;
  publicKey?: string;
  fallback?: string;
  customPrefix?: string;
  truncateLength?: number; // New optional prop for truncation length
};

export const getUserDisplayName: (
  props?: getUserDisplayNameProps,
) => string = ({
  extraData,
  username,
  publicKey = "",
  fallback,
  customPrefix = "",
  truncateLength = 25,
} = {}) => {
  const displayName =
    extraData?.DisplayName ||
    getUserName({
      account: {
        username,
        publicKey,
      },
      customPrefix,
      fallback,
    });

  return displayName.length > truncateLength
    ? `${displayName.slice(0, truncateLength)}...`
    : displayName;
};

export function isDesoPublicKey(value?: string) {
  if (!value) return false;

  return value.startsWith("BC") || value.startsWith("tBC");
}
